import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Paper,
  Box,
  Typography,
  Divider,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import Confirmation from "components/ui/ConfirmationDialog/ConfirmationDialog";
import { useNotificationData } from "./useNotificationData";
import { useNotificationActions } from "./useNotificationActions";
import { filterNotifications } from "./Notificationfiltering";
import NotificationHeader from "./NotificationHeader";
import NotificationList from "./NotificationList";
import { useNotificationPolling } from "./useNotificationPolling";
import { groupNotificationsByDate } from "./Notificationgrouping";
import { TASK_STATUS } from "config/constants/constants";

const NotificationModal = ({ open, onClose, setCountOfNotifications, setShowGenerationInProgress }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);
  const [showReadNotifications, setShowReadNotifications] = useState(false);

  // Selectors
  const currentPractitionerId = useSelector((state) => state.auth.practitionerData?.id);
  const isDarkTheme = useSelector((state) => state.app.theme) === "dark";

  // Custom hooks
  const {
    allNotifications,
    isUpdating,
    setIsUpdating,
    loadNotifications,
    setServerNotifications,
  } = useNotificationData(open);

  // Load notifications initially even when modal is closed
  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  useNotificationPolling({
    open,
    loadNotifications,
    allNotifications,
  });

  const {
    handleDelete,
    handleMarkAsUnread,
    handleMarkAsRead,
    handleClick,
    handleRetry,
    clearAll,
  } = useNotificationActions({
    currentPractitionerId,
    allNotifications,
    setIsUpdating,
    loadNotifications,
    onClose,
    navigate,
    dispatch,
    t,
    setServerNotifications,
  });


  useEffect(() => {
    if (!open) return;

    const stuckNotifications = allNotifications.filter(
      (n) =>
        n.type === "background_task" &&
        n.status === TASK_STATUS.FAILED &&
        n.task_category !== "soap" &&   // "soap" always has a footer button
        !n.isRead
    );

    stuckNotifications.forEach((n) => handleMarkAsRead(n));
    // We intentionally only re-run when `open` flips to true so we don't
    // spam the server on every polling cycle while the modal is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // ────────────────────────────────────────────────────────────────────────────

  // Filtered and grouped notifications
  const displayedNotifications = useMemo(() => {
    return filterNotifications(allNotifications, showReadNotifications);
  }, [allNotifications, showReadNotifications]);

  const groupedNotifications = useMemo(() => {
    return groupNotificationsByDate(displayedNotifications, t);
  }, [displayedNotifications, t]);

  // Counts - Calculate unread count INCLUDING in-progress tasks
  const { unreadCount, readCount, totalNotificationCount, hasInProgressTasks } = useMemo(() => {
    const unread = allNotifications.filter(
      n => !n.isRead &&
        (n.type !== "background_task" || ["COMPLETED", "FAILED", "IN_PROGRESS", "QUEUED", "RUNNING", "PENDING"].includes(n.status))
    ).length;

    const read = allNotifications.filter(
      n => n.isRead === true &&
        (n.type !== "background_task" || ["COMPLETED", "FAILED"].includes(n.status))
    ).length;

    const inProgress = allNotifications.some(task =>
      task.type === "background_task" &&
      ["IN_PROGRESS", "QUEUED", "RUNNING", "PENDING"].includes(task.status)
    );

    return {
      unreadCount: unread,
      readCount: read,
      totalNotificationCount: unread + read,
      hasInProgressTasks: inProgress,
    };
  }, [allNotifications]);

  useEffect(() => {
    setCountOfNotifications(unreadCount);
  }, [unreadCount, setCountOfNotifications]);

  // Update generation in progress indicator
  useEffect(() => {
    setShowGenerationInProgress(hasInProgressTasks);
  }, [hasInProgressTasks, setShowGenerationInProgress]);

  const hasAnyNotifications = totalNotificationCount > 0;
  const groupKeys = Object.keys(groupedNotifications);

  const handleClearAll = () => {
    clearAll();
    setShowClearConfirmation(false);
  };

  const hasClearableNotifications = allNotifications.some(
    n => n.type === "background_task" &&
      n.taskId &&
      ["COMPLETED", "FAILED"].includes(n.status)
  );

  const canClearAll = !hasInProgressTasks;

  return (
    <>
      <Confirmation
        open={showClearConfirmation}
        setOpen={setShowClearConfirmation}
        message={t("notifications.UNREAD_CONFIRMATION")}
        callback={handleClearAll}
      />

      <Dialog open={open} onClose={onClose}>
        <Box
          sx={{ position: "fixed", top: "64px", right: "2rem" }}
          className="notification-height"
        >
          <Paper
            sx={{
              width: "350px",
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              borderRadius: "8px",
            }}
          >
            <NotificationHeader
              showReadNotifications={showReadNotifications}
              setShowReadNotifications={setShowReadNotifications}
              unreadCount={unreadCount}
              readCount={readCount}
              hasAnyNotifications={hasAnyNotifications}
              onClearAll={() => setShowClearConfirmation(true)}
              isDarkTheme={isDarkTheme}
              canClearAll={canClearAll}
            />

            <Divider sx={{ borderBottomWidth: "1px", borderColor: "text.secondary" }} />

            <NotificationList
              groupedNotifications={groupedNotifications}
              groupKeys={groupKeys}
              onDelete={handleDelete}
              onMarkAsUnread={handleMarkAsUnread}
              onClick={handleClick}
              onRetry={handleRetry}
              showReadNotifications={showReadNotifications}
              isUpdating={isUpdating}
              isDarkTheme={isDarkTheme}
            />

            <DialogActions>
              <Typography variant="body2" color="text.secondary" fontSize="10px">
                {t("notifications.SHOWING_LAST_7_DAYS")}
              </Typography>

              <Button onClick={onClose} color="primary">
                {t("notifications.CLOSE")}
              </Button>
            </DialogActions>
          </Paper>
        </Box>
      </Dialog>
    </>
  );
};

export default NotificationModal;